// src/utils/uploadResponses.js (or .ts)
/* ------------------------------------------------------------------ */
/* uploadResponses – questionnaire submission helper                  */
/* ------------------------------------------------------------------ */

import { submitRegionalQuestionnaireResponse } from "../backend/pdxBackendClient";
import { captureCurrentEnvironmentalContext } from "../environmentalContext";

/**
 * uploadResponses
 * ---------------
 * Submits a questionnaire-response JSON payload through the regional backend
 * so validation and persistence remain service-owned.
 *
 * @param {string}  questionnaireName   Slug for the questionnaire (e.g. `"respHealthAssessment"`).
 * @param {object}  formData            Flat `{ id: value }` map of answers.
 * @param {{
 *   locale?: string,
 *   contentVersion?: string
 * } & Parameters<typeof submitRegionalQuestionnaireResponse>[2]} [options={}]
 *        Optional submission metadata and request overrides.
 * @returns {Promise<unknown>}          Resolves when the backend submission completes.
 */
export const uploadResponses = async (
  questionnaireName,
  formData,
  options = {},
) => {
  const {
    locale = null,
    contentVersion = null,
    environmentalContext = undefined,
    ...requestOptions
  } = options;
  const payload = {
    responses: formData,
    submittedAt: new Date().toISOString(),
  };

  if (locale) {
    payload.locale = locale;
  }

  if (contentVersion) {
    payload.contentVersion = contentVersion;
  }

  const resolvedEnvironmentalContext =
    environmentalContext === undefined
      ? await captureCurrentEnvironmentalContext({ purpose: "questionnaire" })
      : environmentalContext;

  if (resolvedEnvironmentalContext) {
    payload.environmentalContext = resolvedEnvironmentalContext;
  }

  return submitRegionalQuestionnaireResponse(
    questionnaireName,
    payload,
    requestOptions,
  );
};
